import { useEffect, useMemo, useState } from "react";
import { api, fmtTime, toast, useLoad, usePoll } from "../api";
import { Empty, HowTo, PageHead, Tip, useMe, useStatus } from "../ui";
import { ControlCard, EventsCard, MonitorsCard, OnAirCard, OutputsCard, PlaysBanner, RecentCard } from "./widgets";

/** Problems first, each with the place to fix it. */
function Diagnosis({ engine }: { engine: any }) {
  const issues: [string, string, string][] = [];
  if (!engine || engine.state === "nicht erreichbar") issues.push(["bad", "Die Engine antwortet nicht.", "#/verwaltung/ereignisse"]);
  if (engine?.config_source === "cache") issues.push(["warn", "Die Engine läuft mit zwischengespeicherten Einstellungen (API war beim Start nicht erreichbar).", "#/sender/main/sendung"]);
  for (const o of engine?.outputs ?? []) if (o.state === "fehler") issues.push(["bad", `Ausgang „${o.name}“: ${o.error ?? "gestört"}`, "#/einstellungen/ausgaenge"]);
  if (engine?.silence?.silent) issues.push(["bad", "Auf dem Sendeweg ist Stille.", "#/einstellungen/stille"]);
  if (engine?.processor?.state === "fehler") issues.push(["warn", `StereoTool: ${engine.processor.error}`, "#/einstellungen/klang"]);
  if (engine && engine.state === "läuft" && !(engine.outputs ?? []).some((o: any) => o.state === "verbunden"))
    issues.push(["warn", "Kein Ausgang verbunden – niemand kann zuhören.", "#/einstellungen/ausgaenge"]);
  if (!issues.length) return null;
  return (
    <>
      {issues.map(([cls, text, href]) => (
        <div key={text} className={`banner ${cls}`}>
          ⚠ <span>{text}</span>
          <span className="spacer" />
          <a href={href}>Beheben →</a>
        </div>
      ))}
    </>
  );
}

/** Log health: drift > 90 s with "Beheben", gaps in the next 24 h named by reason. */
function PlanHealth({ health, onFixed }: { health: any; onFixed: () => void }) {
  const me = useMe();
  if (!health) return null;
  const drift = health.drift_s;
  const fix = async () => {
    if (!window.confirm("Drift beheben: Die nächsten 6 Stunden werden gelöscht, neu getimt und neu generiert. Handänderungen und Voicetrack-Slots in diesen Stunden gehen verloren. Fortfahren?")) return;
    try {
      await api("/api/log/fix-drift", { method: "POST" });
      toast("Die nächsten 6 Stunden sind neu geplant");
      onFixed();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  return (
    <>
      {drift != null && Math.abs(drift) > 90 && !health.live && (
        <div className="banner warn">
          ⚠ <span>Der Sender läuft {Math.round(Math.abs(drift))} s {drift > 0 ? "hinter" : "vor"} dem Plan.</span>
          <span className="spacer" />
          {["admin", "programm", "redaktion"].includes(me?.role) && <button className="btn small" onClick={fix}>Beheben</button>}
        </div>
      )}
      {health.gaps?.length > 0 && (
        <div className="banner warn" style={{ display: "block" }}>
          ⚠ {health.gaps.length} Planlücken in den nächsten 24 h <a href="#/plan/sendeplan" style={{ marginLeft: 8 }}>Zum Sendeplan →</a>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
            {health.gaps.slice(0, 5).map((g: any, i: number) => (
              <li key={i} className="small">{fmtTime(g.planned_start)} · {g.label || g.category || "Slot"} – {(g.badges ?? []).filter((b: string) => b !== "nicht gefüllt").join(", ") || "nicht gefüllt"}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

const DAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
const hhmm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/** Next own show with a live countdown. */
function NextShow({ shows }: { shows: any[] }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = window.setInterval(() => setNow(Date.now()), 1000); return () => window.clearInterval(t); }, []);
  const next = useMemo(() => {
    let best: { at: number; s: any } | null = null;
    for (const s of shows) {
      for (let d = 0; d < 8; d++) {
        const day = new Date(now);
        day.setHours(0, 0, 0, 0);
        day.setDate(day.getDate() + d);
        const iso = day.toLocaleDateString("sv-SE");
        const ok = s.date ? String(s.date).slice(0, 10) === iso : (s.days ?? []).includes(day.getDay());
        if (!ok) continue;
        const at = day.getTime() + s.start_min * 60_000;
        if (at + s.duration_min * 60_000 > now && (!best || at < best.at)) best = { at, s };
      }
    }
    return best;
  }, [shows, Math.floor(now / 60_000)]);
  if (!next) return null;
  const left = next.at - now;
  const running = left <= 0;
  const h = Math.floor(Math.abs(left) / 3_600_000), m = Math.floor((Math.abs(left) % 3_600_000) / 60_000), sec = Math.floor((Math.abs(left) % 60_000) / 1000);
  return (
    <div className="card" style={{ background: running ? "#fbe3dc" : undefined }}>
      <div className="small muted">{running ? "Läuft gerade" : "Nächste Sendung"}</div>
      <div className="row"><b style={{ fontSize: 18 }}>{next.s.name}</b><span className="spacer" />
        {!running && <span style={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{h > 0 ? `${h}:` : ""}{String(m).padStart(2, "0")}:{String(sec).padStart(2, "0")}</span>}</div>
      <div className="small muted">{new Date(next.at).toLocaleString("de-DE", { weekday: "long", hour: "2-digit", minute: "2-digit" })} · {next.s.duration_min} min</div>
    </div>
  );
}

/** "Meine Sendungsvorbereitung": own shows, open voicetracks, notes and a checklist (per user). */
function Prep() {
  const { data, reload } = useLoad<any>("/api/prep");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("note");
  const add = async () => {
    await api("/api/prep", { body: { title, kind } });
    setTitle("");
    reload();
  };
  const notes = (data?.notes ?? []).filter((n: any) => n.kind === "note");
  const checks = (data?.notes ?? []).filter((n: any) => n.kind === "check");
  return (
    <div className="grid g2">
      <div className="stack">
        <NextShow shows={data?.shows ?? []} />
        <div className="card">
          <h2>Meine Sendungen</h2>
          {!data?.host ? <Empty>Du bist noch keinem Moderator-Profil zugeordnet (Sender › Moderatoren).</Empty> :
            !data.shows.length ? <Empty>Keine Sendungen eingetragen.</Empty> :
              data.shows.map((s: any) => <div key={s.id} className="row" style={{ padding: "3px 0" }}><b>{hhmm(s.start_min)}</b><span>{s.name}</span><span className="small muted">{s.date ? String(s.date).slice(0, 10) : (s.days ?? []).map((d: number) => DAYS[d]).join(" ")} · {s.duration_min} min</span></div>)}
        </div>
        <div className={`banner ${data?.open_voicetracks ? "warn" : "ok"}`}>
          {data?.open_voicetracks ? `${data.open_voicetracks} Voicetrack-Slots in den nächsten 7 Tagen sind noch leer.` : "Keine offenen Voicetracks."}
          <span className="spacer" /><a href="#/plan/voicetracking">Zu den Voicetracks →</a>
        </div>
      </div>
      <div className="card">
        <h2>Notizen und Checkliste</h2>
        {checks.map((n: any) => (
          <label key={n.id} className="row" style={{ padding: "2px 0" }}>
            <input type="checkbox" checked={n.done} onChange={async (e) => { await api(`/api/prep/${n.id}`, { method: "PUT", body: { done: e.target.checked } }); reload(); }} />
            <span style={{ textDecoration: n.done ? "line-through" : undefined }}>{n.title}</span><span className="spacer" />
            <button className="btn small" onClick={async () => { await api(`/api/prep/${n.id}`, { method: "DELETE" }); reload(); }}>×</button>
          </label>
        ))}
        {notes.map((n: any) => (
          <div key={n.id} className="row" style={{ padding: "4px 0", borderTop: "1px solid var(--line)" }}>
            <span>📝 {n.title}</span><span className="spacer" />
            <button className="btn small" onClick={async () => { await api(`/api/prep/${n.id}`, { method: "DELETE" }); reload(); }}>×</button>
          </div>
        ))}
        <div className="row" style={{ marginTop: 8 }}>
          <select style={{ width: 130 }} value={kind} onChange={(e) => setKind(e.target.value)}><option value="note">Notiz</option><option value="check">Aufgabe</option></select>
          <input type="text" placeholder="z. B. Gast anrufen, Gewinnspiel vorbereiten" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && title.trim() && add()} />
          <button className="btn" disabled={!title.trim()} onClick={add}>+</button>
        </div>
      </div>
    </div>
  );
}

/** Einrichtungs-Checkliste: 6 Meilensteine bis zur ersten Sendung aus dem Sendeplan; minimierbar, verschwindet danach. */
function SetupChecklist({ engine, health }: { engine: any; health: any }) {
  const { data: lib } = useLoad<any>("/api/library/health");
  const { data: cats } = useLoad<any[]>("/api/categories");
  const { data: clocks } = useLoad<any[]>("/api/clocks");
  const { data: grid } = useLoad<any>("/api/grid");
  const [small, setSmall] = useState(() => { try { return localStorage.getItem("rc-setup-min") === "1"; } catch { return false; } });
  const steps: [boolean, string, string][] = [
    [(engine?.outputs ?? []).some((o: any) => o.state === "verbunden"), "Ausgang verbunden", "#/einstellungen/ausgaenge"],
    [(lib?.all ?? 0) > 0, "Musik in der Bibliothek", "#/bibliothek"],
    [(cats ?? []).some((c: any) => c.kind === "music" && c.songs > 0), "Ordner (Kategorie) mit Titeln", "#/bibliothek"],
    [(clocks ?? []).some((c: any) => (c.elements ?? c.els ?? []).length > 0 || c.element_count > 0 || c.elements_count > 0) || (clocks ?? []).length > 0, "Uhr angelegt", "#/plan/uhren"],
    [(grid?.cells ?? []).some((c: any) => c.clock_id), "Wochenraster belegt", "#/plan/wochenraster"],
    [health?.plays === "log", "Engine spielt den Sendeplan", "#/plan/sendeplan"],
  ];
  const done = steps.filter((x) => x[0]).length;
  if (done === steps.length) return null;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="row"><h2 style={{ margin: 0 }}>Einrichtung: {done} von {steps.length}</h2><span className="spacer" />
        <button className="btn small" onClick={() => { const v = !small; setSmall(v); try { localStorage.setItem("rc-setup-min", v ? "1" : "0"); } catch { /* egal */ } }}>{small ? "Aufklappen" : "Minimieren"}</button></div>
      {!small && (
        <div className="row wrap" style={{ gap: 8, marginTop: 8 }}>
          {steps.map(([ok, label, href]) => <a key={label} href={href} className={`badge ${ok ? "ok" : "warn"}`} style={{ textDecoration: "none" }}>{ok ? "✓" : "○"} {label}</a>)}
        </div>
      )}
    </div>
  );
}

const NEWS_VERSION = "stufe6b";
function WhatsNew() {
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem("rc-news") === NEWS_VERSION; } catch { return false; } });
  if (hidden) return null;
  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="row"><h2 style={{ margin: 0 }}>Was ist neu</h2><span className="spacer" />
        <button className="btn small" onClick={() => { try { localStorage.setItem("rc-news", NEWS_VERSION); } catch { /* egal */ } setHidden(true); }}>Ausblenden</button></div>
      <ul className="small" style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        <li><b>NEU</b> Senderverbund: weitere Sender, Musik übernehmen mit eigener Verpackung, Sendenachweis je Sender</li>
        <li><b>NEU</b> Sendeplan mit Wellenformen und Übergangs-Editor (⇄), große Restzeit-Anzeige</li>
        <li><b>NEU</b> Voicetrack-Studio: Mikro-Knopf mit Einzähler, drei Wellenform-Spuren, Musik ducken, VT für mehrere Sender</li>
        <li><b>NEU</b> Sendezeiten für Songs, Clips, Pools und Kategorien (z. B. nur am Wochenende); Pools füllen sich selbst</li>
        <li><b>NEU</b> Saisons im Wochenraster, Optimieren im Sendeplan, Tempo-Fenster (Stunde geht genau auf)</li>
        <li><b>NEU</b> Seitenbau für die öffentliche Seite, Länder-Statistik, Einladungen per E-Mail, DTMF in Relays</li>
        <li><b>VERBESSERT</b> StereoTool kann jetzt direkt in der Engine laufen (Einstellungen › Klang)</li>
        <li><b>BEHOBEN</b> Sendeplan blieb ohne Imaging-Regeln leer; Bindestriche vor Interpreten</li>
      </ul>
    </div>
  );
}

export function Dashboard() {
  const [view, setView] = useState<"betrieb" | "prep">("betrieb");
  const { data: listeners } = usePoll<any>("/api/stats/now", 60_000);
  const status = useStatus();
  const { data: health } = usePoll<any>("/api/log/health", 60_000);
  const e = status?.engine;
  const outs = e?.outputs ?? [];
  const live = e?.on_air?.kind === "stream";
  return (
    <div>
      <PageHead title="Dashboard" sub="Betrieb · aktualisiert alle 2 Sekunden, pausiert im Hintergrund-Tab" />
      <div className="tabs">
        <a href="#/" className={view === "betrieb" ? "active" : ""} onClick={(ev) => { ev.preventDefault(); setView("betrieb"); }}>Betrieb</a>
        <a href="#/" className={view === "prep" ? "active" : ""} onClick={(ev) => { ev.preventDefault(); setView("prep"); }}>Meine Sendungsvorbereitung</a>
      </div>
      {view === "prep" ? <Prep /> : <>
      <SetupChecklist engine={e} health={health} />
      <WhatsNew />
      <HowTo text="Hier siehst du, wer gerade on air ist, ob alle Ausgänge senden und was zuletzt passiert ist." chapter="dashboard" />
      <Diagnosis engine={e} />
      <PlaysBanner health={health} />
      <PlanHealth health={health} onFixed={() => undefined} />
      <div className="grid g4" style={{ marginBottom: 14 }}>
        <div className="card kpi"><span className="v">{listeners ? listeners.unique_10min ?? listeners.listeners : "–"}</span><span className="l">Hörer jetzt</span></div>
        <div className="card kpi"><span className="v">{live ? 1 : 0}</span><span className="l">Live jetzt</span></div>
        <div className="card kpi"><span className="v">{outs.filter((o: any) => o.state === "verbunden").length} / {outs.length}</span><span className="l">Ausgänge verbunden</span></div>
        <div className="card kpi"><span className="v">{health?.gaps?.length ?? "–"}</span><span className="l">Planlücken 24 h</span></div>
      </div>
      <Tip>Hörer jetzt = verschiedene Geräte der letzten 10 Minuten (wenn ein Statistik-Login gesetzt ist), sonst gleichzeitige Hörer. Planlücken zählen nur Musik-Slots, die nicht gefüllt werden konnten – bewusst leere Elemente (Live, Netz) zählen nicht.</Tip>
      <div className="grid g2">
        <div className="stack">
          <OnAirCard engine={e} />
          <OutputsCard engine={e} />
          <MonitorsCard engine={e} />
        </div>
        <div className="stack">
          <ControlCard engine={e} />
          <RecentCard recent={status?.recent} />
          <EventsCard limit={10} />
        </div>
      </div>
      </>}
    </div>
  );
}
